import { useEffect, useRef } from 'react';
import safeWindow from '../utils/safeWindows';
import { DeviceType } from '../context/chat-widget-context/types';

interface IframeObserverOptions {
  rightOffset?: string;
  isEnabled?: boolean;
  deviceType?: DeviceType;
}

// TODO: maybe this hook can be removed and use directly the functions events isMinimized and isMaximized for optimization.

/**
 * Hook to observe the Salesforce Messaging iframe and apply style overrides.
 * Salesforce inyects the chat widget as a div#embedded-messaging which contains
 * an iframe#embeddedMessagingFrame.
 */
export const useSalesforceIframeObserver = ({
  rightOffset = '30px',
  deviceType = DeviceType.DESKTOP,
}: IframeObserverOptions = {}) => {
  const observerRef = useRef<MutationObserver | null>(null);

  const applyStyles = (iframe: HTMLIFrameElement) => {
    if (iframe && iframe.classList.contains('isMinimized')) {
      iframe.style.removeProperty('right');
    }

    if (deviceType === DeviceType.MOBILE) {
      iframe.style.removeProperty('right');
    }

    if (iframe && iframe.classList.contains('isMaximized') && deviceType !== DeviceType.MOBILE) {
      iframe.style.setProperty('right', rightOffset, 'important');
    }
  };

  useEffect(() => {
    if (!safeWindow) return;

    const targetId = 'embedded-messaging';
    const iframeId = 'embeddedMessagingFrame';

    const checkAndStyle = () => {
      const container = document.getElementById(targetId);
      if (container) {
        const iframe = document.getElementById(iframeId) as HTMLIFrameElement;
        if (iframe) {
          applyStyles(iframe);
          return true;
        }
      }
      return false;
    };

    // 1. Initial check
    checkAndStyle();

    // 2. Setup MutationObserver to watch for DOM changes and class changes
    observerRef.current = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          checkAndStyle();
        }
      }
    });

    observerRef.current.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [rightOffset, deviceType]);

  return null;
};
